import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "w-full px-2 sm:px-6 py-4 space-y-8" }
const _hoisted_2 = {
  key: 0,
  class: "-mt-4"
}
const _hoisted_3 = { class: "space-y-6" }
const _hoisted_4 = {
  key: 0,
  class: "text-center py-12"
}
const _hoisted_5 = {
  key: 1,
  class: "text-center py-12"
}
const _hoisted_6 = {
  key: 2,
  class: "space-y-4"
}
const _hoisted_7 = {
  key: 0,
  class: "text-sm text-black"
}
const _hoisted_8 = {
  key: 1,
  class: "mb-4 text-sm text-black"
}
const _hoisted_9 = {
  key: 2,
  class: "mb-4 text-sm text-black"
}
const _hoisted_10 = { key: 3 }
const _hoisted_11 = ["value"]
const _hoisted_12 = {
  key: 4,
  class: "text-sm text-red-600"
}
const _hoisted_13 = { class: "flex justify-end gap-3 pt-5" }
const _hoisted_14 = ["disabled"]

import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import {
  PageHeader,
  UnifiedPagination,
  ModalComp,
  GammeCreationCard,
  GammeCard,
  GammeEditForm,
} from "@/components";
import {
  getGammesCompletesByVendeur,
  createGammeComplete,
  updateGammeComplete,
  declinaisonHasProducts,
  deleteGammeComplete,
} from "@/api/gammesTailles";
import { useAuthStore } from "@/stores/auth";
import { useNotification } from "@/composables/useNotification";
import type { GammeComplete, CreateGammeData } from "@/types";
import { useRouter } from "vue-router";
import { formatDeclinaisonLabel } from "@/utils/declinaisonLabel";

// Store d'authentification

export default /*@__PURE__*/_defineComponent({
  __name: 'SizeRangeView',
  setup(__props) {

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

// Déterminer la page de retour selon le paramètre 'from'
const fromPage = computed(() => route.query.from as string | undefined);
const returnRoute = computed(() => {
  if (fromPage.value === 'mel') return '/catalog/mel';
  return null;
});
// Notifications
const { success: showSuccess, error: showError } = useNotification();

// État global de la page
const gammes = ref<GammeComplete[]>([]);
const allDeclinaisons = ref<GammeComplete[]>([]);
const currentPage = ref(1);
const itemsPerPage = ref(5);
const isLoading = ref(false);

// État de la modal de modification
const showEditModal = ref(false);
const editingGamme = ref<GammeComplete | null>(null);
const showDeleteModal = ref(false);
const deletingGamme = ref<GammeComplete | null>(null);
const replacementGammeId = ref<number | "">("");
const isDeleting = ref(false);
const isCheckingUsage = ref(false);
const hasBoundProducts = ref<boolean | null>(null);

const getGammeSizes = (gamme: GammeComplete): string[] =>
  [
    gamme.d1_FR,
    gamme.d2_FR,
    gamme.d3_FR,
    gamme.d4_FR,
    gamme.d5_FR,
    gamme.d6_FR,
    gamme.d7_FR,
    gamme.d8_FR,
    gamme.d9_FR,
    gamme.d10_FR,
    gamme.d11_FR,
    gamme.d12_FR,
  ]
    .map((size) => size?.trim())
    .filter((size): size is string => Boolean(size));

const replacementGammes = computed(() => {
  if (!deletingGamme.value) return [];
  const sizeCount = getGammeSizes(deletingGamme.value).length;
  return gammes.value.filter(
    (gamme) =>
      Number(gamme.id_declinaison) !==
        Number(deletingGamme.value?.id_declinaison) &&
      getGammeSizes(gamme).length === sizeCount,
  );
});

const getGammeLabel = (gamme: GammeComplete): string =>
  formatDeclinaisonLabel(gamme.titre?.trim() || "Sans titre", getGammeSizes(gamme));

const replacementOptions = computed(() => {
  return replacementGammes.value.map((gamme) => ({
    gamme,
    label: getGammeLabel(gamme),
  }));
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage.value, gammes.value.length),
);
const paginatedGammes = computed(() => {
  return gammes.value.slice(startIndex.value, endIndex.value);
});

// Méthodes de gestion des données
const loadGammes = async () => {
  if (!authStore.user?.id_vendeur) {
    return;
  }

  isLoading.value = true;

  try {
    // Charger uniquement les gammes du vendeur
    const gammesData = await getGammesCompletesByVendeur(authStore.user.id_vendeur);
    gammes.value = gammesData;
    // Les templates "déclinaison existante" sont limités au vendeur courant
    allDeclinaisons.value = gammesData;
  } catch (err) {
    showError("Erreur lors du chargement des données");
  } finally {
    isLoading.value = false;
  }
};

const handleCreateGamme = async (formData: CreateGammeData) => {
  if (!authStore.user?.id_vendeur) {
    return;
  }

  try {
    const newGamme = await createGammeComplete(
      formData,
      authStore.user.id_vendeur,
    );
    gammes.value = [newGamme, ...gammes.value];
    currentPage.value = 1;

    showSuccess(`Gamme "${formData.titre}" créée avec succès !`);
  } catch (err) {
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la création de la gamme",
    );
  }
};

const handleEditGamme = (gamme: GammeComplete) => {
  editingGamme.value = { ...gamme };
  showEditModal.value = true;
};

const handleDeleteGamme = async (gamme: GammeComplete) => {
  deletingGamme.value = gamme;
  replacementGammeId.value = "";
  hasBoundProducts.value = null;
  showDeleteModal.value = true;
  isCheckingUsage.value = true;
  try {
    hasBoundProducts.value = await declinaisonHasProducts(
      Number(gamme.id_declinaison),
    );
  } catch (err) {
    showDeleteModal.value = false;
    deletingGamme.value = null;
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la vérification de la gamme",
    );
  } finally {
    isCheckingUsage.value = false;
  }
};

const closeDeleteModal = (visible: boolean) => {
  if (isDeleting.value) return;
  showDeleteModal.value = visible;
  if (!visible) {
    deletingGamme.value = null;
    replacementGammeId.value = "";
    hasBoundProducts.value = null;
  }
};

const confirmDeleteGamme = async () => {
  if (
    !deletingGamme.value ||
    hasBoundProducts.value === null ||
    (hasBoundProducts.value && !replacementGammeId.value) ||
    isDeleting.value
  ) {
    return;
  }

  isDeleting.value = true;
  try {
    const deletedTitle = deletingGamme.value.titre || "Sans titre";
    await deleteGammeComplete(
      Number(deletingGamme.value.id_declinaison),
      hasBoundProducts.value ? Number(replacementGammeId.value) : null,
    );
    showDeleteModal.value = false;
    deletingGamme.value = null;
    replacementGammeId.value = "";
    hasBoundProducts.value = null;
    await loadGammes();
    currentPage.value = Math.min(
      currentPage.value,
      Math.max(1, Math.ceil(gammes.value.length / itemsPerPage.value)),
    );
    showSuccess(`Gamme "${deletedTitle}" supprimée avec succès !`);
  } catch (err) {
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la suppression de la gamme",
    );
  } finally {
    isDeleting.value = false;
  }
};

const handleEditSubmit = async (updatedGamme: GammeComplete) => {
  try {
    // S'assurer que id_declinaison est un nombre
    const gammeToUpdate = {
      ...updatedGamme,
      id_declinaison: parseInt(updatedGamme.id_declinaison.toString()),
    };

    const result = await updateGammeComplete(gammeToUpdate);

    // Convertir les deux en nombres pour la comparaison
    const idToFind = parseInt(updatedGamme.id_declinaison.toString());
    const index = gammes.value.findIndex(
      (g) => parseInt(g.id_declinaison.toString()) === idToFind,
    );

    if (index !== -1) {
      gammes.value[index] = result;
      showSuccess(`Gamme "${updatedGamme.titre}" modifiée avec succès !`);
    } else {
      // Recharger toutes les gammes pour être sûr
      await loadGammes();
      showSuccess(`Gamme "${updatedGamme.titre}" modifiée avec succès !`);
    }

    showEditModal.value = false;
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : "Erreur lors de la modification de la gamme";
    console.error("Modification gamme:", err);
    showError(message);
  }
};

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: "auth" });
    return;
  }
  await loadGammes();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Gestion des Gammes de Tailles",
      subtitle: "",
      class: "mb-0 pr-10"
    }),
    _createElementVNode("div", _hoisted_1, [
      (returnRoute.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("button", {
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push(returnRoute.value))),
              class: "inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors"
            }, _cache[9] || (_cache[9] = [
              _createElementVNode("i", { class: "fas fa-arrow-left mr-2" }, null, -1),
              _createTextVNode(" Retour à la création de produit ")
            ]))
          ]))
        : _createCommentVNode("", true),
      _createVNode(_unref(GammeCreationCard), {
        "all-declinaisons": allDeclinaisons.value,
        onCreate: handleCreateGamme
      }, null, 8, ["all-declinaisons"]),
      _createElementVNode("div", _hoisted_3, [
        _cache[12] || (_cache[12] = _createElementVNode("h2", { class: "text-xl font-semibold text-black" }, "Gammes existantes", -1)),
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[10] || (_cache[10] = [
              _createElementVNode("div", { class: "text-black" }, [
                _createElementVNode("i", { class: "fas fa-spinner fa-spin text-4xl mb-4" }),
                _createElementVNode("p", null, "Chargement des gammes...")
              ], -1)
            ])))
          : (gammes.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[11] || (_cache[11] = [
                _createElementVNode("div", { class: "text-black" }, [
                  _createElementVNode("i", { class: "fas fa-ruler text-4xl mb-4" }),
                  _createElementVNode("p", null, "Aucune gamme de tailles créée"),
                  _createElementVNode("p", { class: "text-sm" }, "Créez votre première gamme ci-dessus")
                ], -1)
              ])))
            : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(_unref(UnifiedPagination), {
                  "total-items": gammes.value.length,
                  "current-page": currentPage.value,
                  "items-per-page": itemsPerPage.value,
                  variant: "product",
                  "item-label": "gammes de tailles",
                  "onUpdate:currentPage": _cache[1] || (_cache[1] = ($event: any) => (currentPage.value = $event)),
                  "onUpdate:itemsPerPage": _cache[2] || (_cache[2] = ($event: any) => (itemsPerPage.value = $event))
                }, null, 8, ["total-items", "current-page", "items-per-page"]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedGammes.value, (gamme) => {
                  return (_openBlock(), _createBlock(_unref(GammeCard), {
                    key: gamme.id_declinaison,
                    gamme: gamme,
                    onEdit: handleEditGamme,
                    onDelete: handleDeleteGamme
                  }, null, 8, ["gamme"]))
                }), 128))
              ]))
      ]),
      _createVNode(_unref(UnifiedPagination), {
        "total-items": gammes.value.length,
        "current-page": currentPage.value,
        "items-per-page": itemsPerPage.value,
        variant: "product",
        "item-label": "gammes de tailles",
        "onUpdate:currentPage": _cache[3] || (_cache[3] = ($event: any) => (currentPage.value = $event)),
        "onUpdate:itemsPerPage": _cache[4] || (_cache[4] = ($event: any) => (itemsPerPage.value = $event))
      }, null, 8, ["total-items", "current-page", "items-per-page"])
    ]),
    _createVNode(_unref(ModalComp), {
      "is-visible": showEditModal.value,
      "onUpdate:isVisible": _cache[6] || (_cache[6] = ($event: any) => (showEditModal.value = $event)),
      title: "Modifier la gamme de tailles",
      size: "lg",
      "hide-footer": true
    }, {
      body: _withCtx(() => [
        (editingGamme.value)
          ? (_openBlock(), _createBlock(_unref(GammeEditForm), {
              key: 0,
              gamme: editingGamme.value,
              onSubmit: handleEditSubmit,
              onCancel: _cache[5] || (_cache[5] = ($event: any) => (showEditModal.value = false))
            }, null, 8, ["gamme"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(_unref(ModalComp), {
      "is-visible": showDeleteModal.value,
      title: "Supprimer la gamme de tailles",
      size: "sm",
      "hide-footer": true,
      "onUpdate:isVisible": closeDeleteModal
    }, {
      body: _withCtx(() => [
        (isCheckingUsage.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, " Vérification des produits associés... "))
          : (hasBoundProducts.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
                _cache[13] || (_cache[13] = _createTextVNode(" Tous les produits utilisant ")),
                _createElementVNode("strong", null, "« " + _toDisplayString(deletingGamme.value?.titre || "Sans titre") + " »", 1),
                _cache[14] || (_cache[14] = _createTextVNode(" seront associés à la gamme sélectionnée. "))
              ]))
            : (hasBoundProducts.value === false)
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, " Cette gamme n'est associée à aucun produit. Elle peut être supprimée sans remplacement. "))
              : _createCommentVNode("", true),
        (hasBoundProducts.value && replacementOptions.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _cache[16] || (_cache[16] = _createElementVNode("label", { class: "block mb-2 text-sm font-medium text-black" }, " Gamme de remplacement ", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((replacementGammeId).value = $event)),
                class: "w-full px-3 py-2 border border-gray-300 rounded-md"
              }, [
                _cache[15] || (_cache[15] = _createElementVNode("option", { value: "" }, "Sélectionner une gamme", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(replacementOptions.value, (option) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: option.gamme.id_declinaison,
                    value: option.gamme.id_declinaison
                  }, _toDisplayString(option.label), 9, _hoisted_11))
                }), 128))
              ], 512), [
                [
                  _vModelSelect,
                  replacementGammeId.value,
                  void 0,
                  { number: true }
                ]
              ])
            ]))
          : (hasBoundProducts.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_12, " Créez d'abord une autre gamme avec le même nombre de tailles. "))
            : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("button", {
            type: "button",
            class: "px-4 py-2 text-sm border border-gray-300 rounded-md",
            onClick: _cache[8] || (_cache[8] = ($event: any) => (closeDeleteModal(false)))
          }, " Annuler "),
          _createElementVNode("button", {
            type: "button",
            disabled: 
            isDeleting.value ||
            isCheckingUsage.value ||
            hasBoundProducts.value === null ||
            (hasBoundProducts.value && !replacementGammeId.value)
          ,
            class: "px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed",
            onClick: confirmDeleteGamme
          }, _toDisplayString(isDeleting.value ? "Suppression..." : "Supprimer"), 9, _hoisted_14)
        ])
      ]),
      _: 1
    }, 8, ["is-visible"])
  ], 64))
}
}

})